import React from 'react';

export type LessonFilter = 'all' | 'available';

export type UseFiltersOptions = {
  initialLessonFilter?: LessonFilter;
  initialTeacher?: string;
  initialStudent?: string;
  initialDate?: string; // yyyy-MM-dd
  initialStartDate?: string;
  initialEndDate?: string;
  initialLessonType?: string;
  initialLicenseType?: string;
};

export type UseFiltersReturn = {
  // Local (staged) values
  lessonFilter: LessonFilter;
  setLessonFilter: React.Dispatch<React.SetStateAction<LessonFilter>>;
  teacherFilter: string;
  setTeacherFilter: React.Dispatch<React.SetStateAction<string>>;
  studentFilter: string;
  setStudentFilter: React.Dispatch<React.SetStateAction<string>>;
  selectedDate: string;
  setSelectedDate: React.Dispatch<React.SetStateAction<string>>;
  startDate: string;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  endDate: string;
  setEndDate: React.Dispatch<React.SetStateAction<string>>;
  lessonType: string;
  setLessonType: React.Dispatch<React.SetStateAction<string>>;
  licenseType: string;
  setLicenseType: React.Dispatch<React.SetStateAction<string>>;

  // Applied values (for actual filtering)
  appliedFilters: {
    lessonFilter: LessonFilter;
    teacherFilter: string;
    studentFilter: string;
    selectedDate: string;
    startDate: string;
    endDate: string;
    lessonType: string;
    licenseType: string;
  };

  applyFilters: () => void;
  resetFilters: () => void;
  clearDate: () => void;
};

export function useFilters(options: UseFiltersOptions = {}): UseFiltersReturn {
  const {
    initialLessonFilter = 'all',
    initialTeacher = 'all',
    initialStudent = 'all',
    initialDate = '',
    initialStartDate = '',
    initialEndDate = '',
    initialLessonType = '',
    initialLicenseType = '',
  } = options;

  const [lessonFilter, setLessonFilter] = React.useState<LessonFilter>(initialLessonFilter);
  const [teacherFilter, setTeacherFilter] = React.useState<string>(initialTeacher);
  const [studentFilter, setStudentFilter] = React.useState<string>(initialStudent);
  const [selectedDate, setSelectedDate] = React.useState<string>(initialDate);
  const [startDate, setStartDate] = React.useState<string>(initialStartDate);
  const [endDate, setEndDate] = React.useState<string>(initialEndDate);
  const [lessonType, setLessonType] = React.useState<string>(initialLessonType);
  const [licenseType, setLicenseType] = React.useState<string>(initialLicenseType);

  const [appliedFilters, setAppliedFilters] = React.useState({
    lessonFilter: initialLessonFilter,
    teacherFilter: initialTeacher,
    studentFilter: initialStudent,
    selectedDate: initialDate,
    startDate: initialStartDate,
    endDate: initialEndDate,
    lessonType: initialLessonType,
    licenseType: initialLicenseType,
  });

  const applyFilters = React.useCallback(() => {
    setAppliedFilters({
      lessonFilter,
      teacherFilter,
      studentFilter,
      selectedDate,
      startDate,
      endDate,
      lessonType,
      licenseType,
    });
  }, [lessonFilter, teacherFilter, studentFilter, selectedDate, startDate, endDate, lessonType, licenseType]);

  const resetFilters = React.useCallback(() => {
    setLessonFilter(initialLessonFilter);
    setTeacherFilter(initialTeacher);
    setStudentFilter(initialStudent);
    setSelectedDate(initialDate);
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    setLessonType(initialLessonType);
    setLicenseType(initialLicenseType);

    setAppliedFilters({
      lessonFilter: initialLessonFilter,
      teacherFilter: initialTeacher,
      studentFilter: initialStudent,
      selectedDate: initialDate,
      startDate: initialStartDate,
      endDate: initialEndDate,
      lessonType: initialLessonType,
      licenseType: initialLicenseType,
    });
  }, [initialLessonFilter, initialTeacher, initialStudent, initialDate, initialStartDate, initialEndDate, initialLessonType, initialLicenseType]);

  const clearDate = React.useCallback(() => {
    setSelectedDate('');
    setStartDate('');
    setEndDate('');
  }, []);

  return {
    lessonFilter,
    setLessonFilter,
    teacherFilter,
    setTeacherFilter,
    studentFilter,
    setStudentFilter,
    selectedDate,
    setSelectedDate,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    lessonType,
    setLessonType,
    licenseType,
    setLicenseType,
    appliedFilters,
    applyFilters,
    resetFilters,
    clearDate,
  };
}